/**
 * The hideout in words (WP-X4, docs/HIDEOUT.md): the furnishing cards, the trophy wall's lines and the gang's
 * history, for the room's panel and the book's "Hideout" page. Pure: no DOM, unit-tested.
 */
import { HIDEOUT_ITEMS, type HideoutItemDef } from '../../shared/hideout';
import { weekStart } from '../../shared/markets';
import type { HideoutHistoryView, HideoutView } from '../../shared/messages/hideout';
import { weekOf } from '../book/weekPage';
import { districtName } from '../online-ui/economyFormat';

const n = (v: number): string => Math.round(v).toLocaleString('en-GB');
export const pounds = (v: number): string => `£${n(v)}`;

export interface ItemCard {
  def: HideoutItemDef;
  owned: boolean;
  /** Who bought it ("Bought by Ada"), or the price. */
  sub: string;
  /** The button: null when there is nothing to do (owned). */
  action: { label: string; enabled: boolean } | null;
  /** Why the button is greyed, in plain words ('' when it isn't). */
  why: string;
}

/** One card per furnishing, in catalogue order: owned, buyable, or why not. */
export function itemCards(v: HideoutView): ItemCard[] {
  return HIDEOUT_ITEMS.map((def) => {
    const got = v.items.find((i) => i.id === def.id);
    if (got) return { def, owned: true, sub: got.by ? `Bought by ${got.by}` : 'In the hideout', action: null, why: '' };
    const short = Math.max(0, def.price - v.treasury);
    const why = !v.you.furnish ? 'Your role can’t buy for the hideout (it needs “Hideout” in the gang ledger’s roles).' : short > 0 ? `The treasury is ${pounds(short)} short.` : '';
    return { def, owned: false, sub: pounds(def.price), action: { label: `Buy · ${pounds(def.price)}`, enabled: why === '' }, why };
  });
}

/** "Kingpin of the quay (the week of 5 October)" — the wall's honours, newest first. */
export function trophyLines(v: HideoutView): string[] {
  return v.trophies.map((t) => `${t.title} (${weekOf(weekStart(t.week))})`);
}

const DAY_MS = 86_400_000;
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const shortDate = (t: number): string => {
  const d = new Date(t);
  return `${d.getUTCDate()} ${MONTHS[d.getUTCMonth()]}`;
};

export function historyLine(h: HideoutHistoryView): string {
  const where = districtName(h.district);
  if (h.kind === 'taken') return `Took ${where} in a contest · ${shortDate(h.at)}`;
  if (h.kind === 'held') return `Held ${where} against a challenger · ${shortDate(h.at)}`;
  return `First ran ${where} · ${shortDate(h.at)}`;
}

/** The gang's record in a few lines: founded, freight carried, contests, districts (what the wall's board says). */
export function recordLines(v: HideoutView, now: number): string[] {
  const days = Math.max(0, Math.floor((now - v.createdAt) / DAY_MS));
  const out = [`Founded ${days === 0 ? 'today' : days === 1 ? 'yesterday' : `${n(days)} days ago`}, ${v.members.length === 1 ? 'one member' : `${n(v.members.length)} members`}.`];
  if (v.freightRuns) out.push(`${v.freightRuns === 1 ? 'One Night Freight run' : `${n(v.freightRuns)} Night Freight runs`} carried.`);
  const won = v.history.filter((h) => h.kind !== 'lead').length;
  if (won) out.push(`${won === 1 ? 'One contest' : `${n(won)} contests`} for a district won.`);
  const led = v.history.filter((h) => h.kind === 'lead').length;
  if (led) out.push(`${led === 1 ? 'One district' : `${n(led)} districts`} run at one time or another.`);
  if (v.trophies.length) out.push(`${v.trophies.length === 1 ? 'One honour' : `${n(v.trophies.length)} honours`} from the Evening Post.`);
  return out;
}

/** Who's in: "Nobody's in.", "Ada's in.", "Ada and Bert are in.", "Ada, Bert and 2 more are in." */
export function whoIsIn(v: HideoutView): string {
  const names = v.inside.map((i) => (i.cid === v.you.cid ? 'you' : i.name));
  if (!names.length) return 'Nobody’s in.';
  const cap = (s: string): string => s.charAt(0).toUpperCase() + s.slice(1);
  if (names.length === 1) return names[0] === 'you' ? 'Just you.' : `${names[0]}’s in.`;
  if (names.length === 2) return `${cap(names[0]!)} and ${names[1]} are in.`;
  return `${cap(names[0]!)}, ${names[1]} and ${names.length - 2} more are in.`;
}

/** What the hideout has and still lacks, in one short status line (the book's tile). */
export function statusLine(v: HideoutView | null): string {
  if (!v) return 'Your gang’s home at 9 Pilcher Street';
  const got = v.items.length;
  if (v.inside.length) return whoIsIn(v).replace(/\.$/, '');
  if (got === HIDEOUT_ITEMS.length) return 'Fully furnished';
  return got ? `${got} of ${HIDEOUT_ITEMS.length} furnished` : 'Bare boards — furnish it from the treasury';
}
